import React, { Component } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import RuleListComponent from './components/RuleListComponent.component';
import {
  getTaskRules,
  updateTaskRule,
  deleteTaskRule,
  generateTasksForRule,
  getTaskTriggerServices,
} from './taskRules.actions';
import {
  GET_TASK_RULES_SUCCESS,
  UPDATE_TASK_RULE_SUCCESS,
  DELETE_TASK_RULE_SUCCESS,
  GENERATE_TASKS_FOR_RULE_SUCCESS,
  GET_TASK_TRIGGER_SERVICES_SUCCESS,
} from './taskRules.types';
import { getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import { GET_ALL_ENUMERATIONS_SUCCESS } from 'modules/enumerations/store/enumeationTypes';
import { getInternalUsers } from 'modules/common/common.actions';
import { GET_INTERNAL_USERS_FAILURE } from 'modules/common/store/types';

class RuleListContainer extends Component {
  static propTypes = {
    history: ReactRouterPropTypes.history.isRequired,
    setPageTitle: PropTypes.func.isRequired,
    showToast: PropTypes.func.isRequired,
    getTaskRules: PropTypes.func.isRequired,
    updateTaskRule: PropTypes.func.isRequired,
    deleteTaskRule: PropTypes.func.isRequired,
    generateTasksForRule: PropTypes.func.isRequired,
    getTaskTriggerServices: PropTypes.func.isRequired,
    getAllEnumerations: PropTypes.func.isRequired,
    getInternalUsers: PropTypes.func.isRequired,
    rules: PropTypes.array,
    isLoadingRules: PropTypes.bool,
    triggerServices: PropTypes.array,
    userId: PropTypes.number,
    allEnums: PropTypes.object,
  };

  componentDidMount() {
    this.props.setPageTitle('Task Rules');
    this.loadRules();
  }

  loadRules = async () => {
    const result = await this.props.getTaskRules();
    if (result.type !== GET_TASK_RULES_SUCCESS) {
      this.props.showToast('Failed to load task rules', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    const enumResult = await this.props.getAllEnumerations();
    if (enumResult.type !== GET_ALL_ENUMERATIONS_SUCCESS) {
      this.props.showToast('Failed to load enumerations', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    // Trigger services resolve the Service column (all catalog rows, inactive included)
    const servicesResult = await this.props.getTaskTriggerServices();
    if (servicesResult.type !== GET_TASK_TRIGGER_SERVICES_SUCCESS) {
      this.props.showToast('Failed to load trigger services', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }

    let { type } = await this.props.getInternalUsers();
    if (type === GET_INTERNAL_USERS_FAILURE) {
      this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    }

  };

  handleCreateNew = () => {
    this.props.history.push('/admin/task-rules/create');
  };

  handleEdit = (taskRuleId) => {
    this.props.history.push(`/admin/task-rules/edit/${taskRuleId}`);
  };

  handleDelete = async (taskRuleId) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this rule? This action cannot be undone. Tasks already created by this rule will NOT be deleted.'
    );

    if (!confirmed) return;

    const result = await this.props.deleteTaskRule(taskRuleId);
    if (result.type === DELETE_TASK_RULE_SUCCESS) {
      this.props.showToast('Rule deleted successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS,
      });
      this.loadRules(); // Refresh list
    } else {
      this.props.showToast('Failed to delete rule', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  handleToggleActive = async (rule) => {
    const updatedRule = {
      ...rule,
      isActive: !rule.isActive,
      modifiedBy: this.props.userId ?? 0,
    };

    const result = await this.props.updateTaskRule(rule.taskRuleId, updatedRule);
    if (result.type === UPDATE_TASK_RULE_SUCCESS) {
      this.props.showToast(
        `Rule ${updatedRule.isActive ? 'activated' : 'deactivated'} successfully`,
        { severity: TOAST_MESSAGE_SEVERITY_SUCCESS }
      );
      this.loadRules(); // Refresh list
    } else {
      this.props.showToast('Failed to update rule status', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  handleGenerateNow = async (taskRuleId) => {
    const confirmed = window.confirm(
      'This will generate tasks for all files/segments that match this rule\'s conditions. Continue?'
    );

    if (!confirmed) return;

    // generatedBy is an InternalUserId; the legacy profile has none, so 0 (system user, same as the nightly sweep) is sent for now
    const result = await this.props.generateTasksForRule(taskRuleId, this.props.userId ?? 0);
    if (result.type === GENERATE_TASKS_FOR_RULE_SUCCESS) {
      const tasksGenerated = result.response?.tasksGenerated || 0;
      this.props.showToast(
        `Successfully generated ${tasksGenerated} task${tasksGenerated !== 1 ? 's' : ''}`,
        { severity: TOAST_MESSAGE_SEVERITY_SUCCESS }
      );
    } else {
      this.props.showToast('Failed to generate tasks', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR,
      });
    }
  };

  render() {
    const { rules, isLoadingRules, allEnums, triggerServices } = this.props;

    if (isLoadingRules && (!rules || rules.length === 0)) {
      return <FullscreenSpinner />;
    }

    return (
      <RuleListComponent
        rules={rules}
        isLoading={isLoadingRules}
        onCreateNew={this.handleCreateNew}
        onEdit={this.handleEdit}
        onDelete={this.handleDelete}
        onToggleActive={this.handleToggleActive}
        onGenerateNow={this.handleGenerateNow}
        allEnums={allEnums}
        triggerServices={triggerServices}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  rules: state.taskRules.rules,
  isLoadingRules: state.taskRules.isLoadingRules,
  triggerServices: state.taskRules.triggerServices,
  userId: state.auth.profile?.internalUserId,
  allEnums: state.enumerations.allEnums,
  internalUsers: state.common.internalUsers,

});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getTaskRules,
    updateTaskRule,
    deleteTaskRule,
    generateTasksForRule,
    getTaskTriggerServices,
    getAllEnumerations,
    getInternalUsers,
  })
)(RuleListContainer);
